import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import {
  Link,
  Navigate,
  createFileRoute,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";

/**
 * `/customers/new` — create a customer. Admin-only (accountants
 * browse + edit existing rows but don't manage the customer
 * list); non-admins are redirected to `/` which then routes
 * them to the right landing for their role.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export const Route = createFileRoute("/_app/customers/new")({
  component: NewCustomerPage,
});

function NewCustomerPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const navigate = useNavigate();
  const createCustomer = useApiMutation(
    (body: { name: string; fakturoidSubjectId: string | undefined }) =>
      api.post("/api/customers", body),
    {
      onSuccess: () => navigate({ to: "/customers" }),
      errorMessage: () => "Couldn't create the customer.",
    },
  );

  if (user.globalRole !== "admin") {
    return <Navigate to="/" />;
  }

  async function create(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) ?? "").trim();
    if (!name) {
      createCustomer.setError("Name is required.");
      return;
    }
    await createCustomer.mutate({
      name,
      fakturoidSubjectId: ((fd.get("fakturoidSubjectId") as string) ?? "").trim() || undefined,
    });
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="text-3xl font-bold">New Customer</h1>
        <p className="text-muted-foreground mt-1">Add a new customer to the system</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Customer Details</CardTitle>
          <CardDescription>Fill in the customer information below.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium leading-none">
                Name <span className="text-destructive">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="Customer name"
                className={INPUT_CLS}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="fakturoidSubjectId" className="text-sm font-medium leading-none">
                Fakturoid Subject ID
              </label>
              <input
                id="fakturoidSubjectId"
                name="fakturoidSubjectId"
                type="number"
                placeholder="Optional"
                className={INPUT_CLS}
              />
            </div>

            {createCustomer.error && (
              <p className="text-sm text-destructive">{createCustomer.error}</p>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={createCustomer.pending}>
                {createCustomer.pending ? "Creating…" : "Create Customer"}
              </Button>
              <Button variant="outline" type="button" asChild>
                <Link to="/customers">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
